import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { useClientAccess } from '../../../hooks/useClientAccess';
import { ProgrammingCommand, ProgrammingChange, CommandOp } from '../../../services/programmingService';
import { DeviceDetailCore } from '../useDeviceDetail';
import { useDevicesDetailsProgramming, StagedChange } from './useDevicesDetailsProgramming';
import { CompositeInput, ConfirmChanges, DisplayValue, ScalarInput, valueText } from './ProgrammingValue';
import './DevicesDetailsProgramming.css';

interface Props {
  core: DeviceDetailCore;
  /** The panel loads its catalogue and pings the gateway once shown. */
  active: boolean;
}

/** An action-only command (reboot, factory reset…): no value, a button in the group's footer. */
const isAction = (command: ProgrammingCommand) => !command.value && command.ops.every((op) => op === 'action');

const matches = (command: ProgrammingCommand, needle: string) =>
  [command.label, command.cmd, command.code, command.legacy_cmd ?? '', command.note ?? '']
    .some((text) => text.toLowerCase().includes(needle));

/**
 * The parameters living in the gateway, read and written over its conf
 * channel. One group at a time, picked in the list on the left (or every
 * group whose parameters match the filter); each group is a table with the
 * value, its state and a read button per row, and the group's actions in
 * a footer. Write mode turns the value column into inputs and gathers the
 * changes in a bar until they are sent, after a confirmation that shows
 * the value before and after.
 */
export const DevicesDetailsProgramming: React.FC<Props> = ({ core, active }) => {
  const { t, formatTime } = useI18n();
  const { canManageDevices, isAnepStaff } = useClientAccess();
  const p = useDevicesDetailsProgramming(core, active);
  const [pending, setPending] = useState<ProgrammingChange[] | null>(null);
  const [rawInput, setRawInput] = useState('');
  const [filter, setFilter] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  // The terminal opens by itself when an exchange starts, so a read or a
  // write is watched as it happens; the user can fold it to a one-line bar.
  const [terminalOpen, setTerminalOpen] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const openedOnce = useRef(false);

  // Opens when an exchange starts, and on the very first frame sent (a
  // fast exchange may be over before the busy state is ever rendered).
  const firstFrameSent = p.log.some((entry) => entry.kind === 'out');
  useEffect(() => {
    if (p.busy || (firstFrameSent && !openedOnce.current)) {
      openedOnce.current = true;
      setTerminalOpen(true);
    }
  }, [p.busy, firstFrameSent]);

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [p.log.length, terminalOpen]);

  const canWrite = canManageDevices;
  const groups = p.catalogue?.groups ?? [];
  const commands = useMemo(() => Object.fromEntries((p.catalogue?.commands ?? []).map((c) => [c.code, c])), [p.catalogue]);
  const clock = (d: Date) => formatTime(d, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  // The first group with parameters is shown until the user picks another.
  const firstGroup = groups.find((g) => (p.commandsByGroup[g.code] ?? []).length > 0)?.code ?? null;
  const currentGroup = selectedGroup && groups.some((g) => g.code === selectedGroup) ? selectedGroup : firstGroup;
  const needle = filter.trim().toLowerCase();
  const filtering = needle.length > 0;

  // What each group shows: its parameters (rows) and its actions (footer),
  // narrowed by the filter when there is one.
  const groupContent = (code: string) => {
    const list = (p.commandsByGroup[code] ?? []).filter((c) => !filtering || matches(c, needle));
    return { rows: list.filter((c) => !isAction(c)), actions: list.filter(isAction) };
  };
  const visibleGroups = groups.filter((g) => {
    const { rows, actions } = groupContent(g.code);
    return filtering ? rows.length + actions.length > 0 : g.code === currentGroup;
  });

  const readable = (code: string) => (p.commandsByGroup[code] ?? []).filter((c) => c.ops.includes('read') && !c.indexed);
  const readCount = (code: string) => readable(code).filter((c) => p.values[c.code]?.status === 'ok').length;
  const stagedCount = (code: string) => (p.commandsByGroup[code] ?? []).filter((c) => p.staged[c.code]).length;

  const confirmAndSend = (changes: ProgrammingChange[]) => setPending(changes);
  const runPending = async () => {
    if (!pending) return;
    try {
      await p.send(pending);
    } catch {
      // logged by the hook
    } finally {
      setPending(null);
    }
  };

  const stageValue = (command: ProgrammingCommand, value: unknown) => {
    const isEmpty = value === '' || value === undefined || (typeof value === 'object' && value !== null && Object.values(value).every((v) => v === '' || v === undefined));
    p.stage(command.code, isEmpty ? null : { op: 'write', value, index: p.indexes[command.code] });
  };

  const onlineClass = p.online === null ? 'unknown' : p.online ? 'online' : 'offline';
  const onlineLabel = p.online === null ? t('devices.programming.stateUnknown') : p.online ? t('devices.programming.online') : t('devices.programming.offline');
  const identLines = (p.identification ?? '').split('\n').map((l) => l.trim()).filter((l) => l.length > 0);

  // ------------------------------------------------------------- one row
  const renderRow = (command: ProgrammingCommand) => {
    const item = p.values[command.code];
    const stagedChange: StagedChange | undefined = p.staged[command.code];
    const editable = p.mode === 'write' && command.ops.includes('write') && command.value;
    const result = p.lastResults.find((r) => r.code === command.code);
    const index = p.indexes[command.code] ?? command.indexed?.min;
    const rowClass = ['prog-row', stagedChange ? 'is-staged' : '', result?.status === 'mismatch' ? 'is-mismatch' : ''].filter(Boolean).join(' ');

    const state = result ? (
      <span className={`prog-badge prog-badge-${result.status}`}>{t(`devices.programming.result.${result.status}` as const)}</span>
    ) : stagedChange ? (
      <span className="prog-badge prog-badge-staged">{t('devices.programming.modified')}</span>
    ) : !command.ops.includes('read') ? (
      <span className="prog-badge prog-badge-none">–</span>
    ) : item ? (
      <span className={`prog-badge prog-badge-${item.status}`} title={item.latency_ms != null ? `${item.latency_ms} ms` : undefined}>
        {t(`devices.programming.read.${item.status}` as const)}
      </span>
    ) : (
      <span className="prog-badge prog-badge-none">{t('devices.programming.read.none')}</span>
    );

    return (
      <tr key={command.code} className={rowClass} data-testid={`programming-row-${command.code}`}>
        <td className="prog-cell-label">
          <div className="prog-label">{command.label}</div>
          <div className="prog-label-meta">
            <span className="prog-code" title={command.legacy_cmd ? `Esse-ti ${command.legacy_cmd}` : undefined}>{command.cmd}</span>
            {command.dangerous && <span className="prog-flag" title={t('devices.programming.dangerous')} aria-label={t('devices.programming.dangerous')}>⚠</span>}
            {command.note && <span className="prog-note">{command.note}</span>}
          </div>
        </td>
        <td className="prog-cell-value">
          {command.indexed && (
            <label className="prog-index">
              <span>{command.indexed.name === 'index' ? t('devices.programming.index') : command.indexed.name}</span>
              <input type="number" min={command.indexed.min} max={command.indexed.max} value={index}
                     onChange={(e) => p.setIndex(command.code, Number(e.target.value))} disabled={p.busy} />
              <span className="prog-help">{command.indexed.min} – {command.indexed.max}</span>
            </label>
          )}
          {editable && command.value ? (
            <>
              {command.value.type === 'composite' ? (
                <CompositeInput id={`prog-${command.code}`} spec={command.value}
                                value={(stagedChange?.value as Record<string, unknown>) ?? (item?.value as Record<string, unknown> | undefined)}
                                onChange={(v) => stageValue(command, v)} disabled={p.busy} />
              ) : (
                <ScalarInput id={`prog-${command.code}`} spec={{ ...command.value, secret: command.value.secret || command.secret }}
                             value={stagedChange?.value ?? (command.secret ? '' : item?.value)}
                             onChange={(v) => stageValue(command, v)} disabled={p.busy} />
              )}
              {stagedChange && item?.status === 'ok' && (
                <div className="prog-before">{t('devices.programming.before')} : {valueText(command.value, item.value, command.secret)}</div>
              )}
            </>
          ) : (
            <DisplayValue command={command} item={item} t={t} />
          )}
          {p.mode === 'write' && (stagedChange || command.ops.includes('delete') || command.ops.includes('clear')) && (
            <div className="prog-value-ops">
              {stagedChange && (
                <button type="button" className="btn btn-link btn-sm" onClick={() => p.stage(command.code, null)} disabled={p.busy}>
                  {t('devices.programming.undo')}
                </button>
              )}
              {(['delete', 'clear'] as CommandOp[]).filter((op) => command.ops.includes(op)).map((op) => (
                <button key={op} type="button" className="btn btn-sm btn-secondary"
                        disabled={p.busy || (command.dangerous && !isAnepStaff)}
                        title={command.dangerous && !isAnepStaff ? t('devices.programming.anepOnly') : undefined}
                        onClick={() => confirmAndSend([{ code: command.code, op, index: op === 'delete' && command.indexed ? index : undefined }])}
                        data-testid={`programming-${op}-${command.code}`}>
                  {t(`devices.programming.op.${op}` as const)}
                </button>
              ))}
            </div>
          )}
        </td>
        <td className="prog-cell-state">{state}</td>
        <td className="prog-cell-actions">
          {command.ops.includes('read') && (
            <button type="button" className="prog-icon-btn" onClick={() => p.readOne(command.code, command.indexed ? index : undefined)}
                    disabled={p.busy} title={t('devices.programming.readRow')} aria-label={t('devices.programming.readRow')}
                    data-testid={`programming-read-${command.code}`}>
              ↻
            </button>
          )}
        </td>
      </tr>
    );
  };

  // ------------------------------------------------------------- one group
  const renderGroup = (group: { code: string; label: string }) => {
    const { rows, actions } = groupContent(group.code);
    const total = readable(group.code).length;
    const read = readCount(group.code);
    const reading = p.readingGroup === group.code;
    return (
      <section key={group.code} className="prog-group" data-testid={`programming-group-${group.code}`}>
        <header className="prog-group-header">
          <h3 className="prog-group-title">{group.label}</h3>
          {total > 0 && (
            <span className={`prog-group-count${read === total ? ' is-complete' : ''}`}>
              {t('devices.programming.readSummary', { read, total })}
            </span>
          )}
          {total > 0 && (
            <button type="button" className="btn btn-secondary btn-sm prog-group-read" onClick={() => p.readGroup(group.code)} disabled={p.busy}
                    data-testid={`programming-read-group-${group.code}`}>
              {reading ? t('devices.programming.reading') : t('devices.programming.readGroup')}
            </button>
          )}
        </header>

        {rows.length > 0 && (
          <table className="prog-table">
            <thead>
              <tr>
                <th className="prog-cell-label">{t('devices.programming.columns.parameter')}</th>
                <th className="prog-cell-value">{t('devices.programming.columns.value')}</th>
                <th className="prog-cell-state">{t('devices.programming.columns.state')}</th>
                <th className="prog-cell-actions"><span className="sr-only">{t('devices.programming.columns.actions')}</span></th>
              </tr>
            </thead>
            <tbody>{rows.map(renderRow)}</tbody>
          </table>
        )}

        {actions.length > 0 && (
          <footer className="prog-group-actions">
            <span className="prog-group-actions-title">{t('devices.programming.actionsTitle')}</span>
            {actions.map((command) => {
              const locked = p.mode !== 'write' ? t('devices.programming.switchToWrite') : command.dangerous && !isAnepStaff ? t('devices.programming.anepOnly') : undefined;
              const result = p.lastResults.find((r) => r.code === command.code);
              return (
                <span key={command.code} className="prog-action">
                  <button type="button" className={`btn btn-sm ${command.dangerous ? 'btn-outline-danger' : 'btn-secondary'}`}
                          disabled={p.busy || locked !== undefined} title={locked ?? command.note}
                          onClick={() => confirmAndSend([{ code: command.code, op: 'action', index: undefined }])}
                          data-testid={`programming-action-${command.code}`}>
                    {command.dangerous && <span aria-hidden="true">⚠ </span>}{command.label}
                    <span className="prog-code">{command.cmd}</span>
                  </button>
                  <span className="prog-action-note">
                    {result && <span className={`prog-badge prog-badge-${result.status}`}>{t(`devices.programming.result.${result.status}` as const)}</span>}
                    {command.note}
                  </span>
                </span>
              );
            })}
          </footer>
        )}
      </section>
    );
  };

  return (
    <div className="modal-content tab-content programming-panel">
      {/* Status strip */}
      <header className="prog-status">
        <div className="prog-status-main">
          <span className={`prog-state prog-state-${onlineClass}`} data-testid="programming-state">
            <span className="prog-dot" />
            {onlineLabel}
          </span>
          {p.online && p.latencyMs != null && <span className="prog-latency">{p.latencyMs} ms</span>}
          {identLines.length > 0 && (
            <details className="prog-ident">
              <summary title={t('devices.programming.identification')}>{identLines[0]}{identLines.length > 1 ? ' …' : ''}</summary>
              <pre>{p.identification}</pre>
            </details>
          )}
          {p.lastReadAt && <span className="prog-last-read">{t('devices.programming.lastRead', { time: clock(p.lastReadAt) })}</span>}
        </div>
        <div className="prog-status-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={p.ping} disabled={p.busy || !p.catalogue} data-testid="programming-ping">
            {p.pinging ? t('devices.programming.pinging') : t('devices.programming.ping')}
          </button>
          <div className="prog-mode" role="group" aria-label={t('devices.programming.modeLabel')}>
            <button type="button" className={`prog-mode-btn${p.mode === 'read' ? ' is-active' : ''}`} onClick={() => p.setMode('read')} data-testid="programming-mode-read">
              {t('devices.programming.modeRead')}
            </button>
            <button type="button" className={`prog-mode-btn${p.mode === 'write' ? ' is-active' : ''}`} onClick={() => p.setMode('write')} disabled={!canWrite} title={canWrite ? undefined : t('devices.programming.writeForbidden')} data-testid="programming-mode-write">
              {t('devices.programming.modeWrite')}
            </button>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={p.readAll} disabled={p.busy || !p.catalogue} data-testid="programming-read-all">
            {p.readProgress ? t('devices.programming.readingProgress', { done: p.readProgress.done, total: p.readProgress.total }) : t('devices.programming.readAll')}
          </button>
        </div>
      </header>

      {p.catalogueFailure === 'none' && <p className="prog-notice prog-notice-muted">{t('devices.programming.noCatalogue')}</p>}
      {p.catalogueFailure === 'forbidden' && <p className="prog-notice prog-notice-error">{t('devices.programming.forbidden')}</p>}
      {p.catalogueFailure === 'failed' && <p className="prog-notice prog-notice-error">{t('devices.programming.failed')}</p>}
      {p.online === false && <p className="prog-notice prog-notice-warning">{t('devices.programming.offlineHint')}</p>}

      {p.mode === 'write' && p.stagedChanges.length > 0 && (
        <div className="prog-staged" data-testid="programming-staged">
          <span className="prog-staged-count">{t('devices.programming.stagedCount', { count: p.stagedChanges.length })}</span>
          <span className="prog-staged-list">
            {p.stagedChanges.map((c) => commands[c.code]?.label ?? c.code).join(' · ')}
          </span>
          <button type="button" className="btn btn-link btn-sm" onClick={() => p.stagedChanges.forEach((c) => p.stage(c.code, null))} disabled={p.writing}>
            {t('devices.programming.discard')}
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => confirmAndSend(p.stagedChanges)} disabled={p.busy} data-testid="programming-send-staged">
            {t('devices.programming.sendStaged')}
          </button>
        </div>
      )}

      {p.catalogue && (
        <div className="prog-body">
          <nav className="prog-nav" aria-label={t('devices.programming.groupsLabel')}>
            <input type="search" className="prog-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
                   placeholder={t('devices.programming.filterPlaceholder')} aria-label={t('devices.programming.filterPlaceholder')}
                   data-testid="programming-filter" />
            {groups.map((group) => {
              const count = (p.commandsByGroup[group.code] ?? []).length;
              if (count === 0) return null;
              const total = readable(group.code).length;
              const read = readCount(group.code);
              const staged = stagedCount(group.code);
              const isCurrent = !filtering && group.code === currentGroup;
              const shown = filtering && visibleGroups.some((g) => g.code === group.code);
              return (
                <button key={group.code} type="button"
                        className={`prog-nav-item${isCurrent ? ' is-active' : ''}${filtering && !shown ? ' is-muted' : ''}`}
                        aria-current={isCurrent ? 'true' : undefined}
                        onClick={() => { setSelectedGroup(group.code); setFilter(''); }}
                        data-testid={`programming-nav-${group.code}`}>
                  <span className="prog-nav-label">{group.label}</span>
                  {staged > 0 && <span className="prog-nav-staged" title={t('devices.programming.stagedCount', { count: staged })}>{staged}</span>}
                  {total > 0 && <span className={`prog-nav-count${read === total ? ' is-complete' : ''}`}>{read}/{total}</span>}
                </button>
              );
            })}
          </nav>

          <div className="prog-main">
            {visibleGroups.map(renderGroup)}
            {filtering && visibleGroups.length === 0 && (
              <p className="prog-notice prog-notice-muted">{t('devices.programming.noMatch', { filter: filter.trim() })}</p>
            )}
          </div>
        </div>
      )}

      {/* The terminal: every frame sent and received, docked at the bottom */}
      {p.catalogue && (
        <section className={`prog-terminal${terminalOpen ? ' is-open' : ''}${p.busy ? ' is-live' : ''}`} aria-label={t('devices.programming.terminal.title')} data-testid="programming-terminal">
          <div className="prog-terminal-bar">
            <button type="button" className="prog-terminal-toggle" onClick={() => setTerminalOpen((o) => !o)} aria-expanded={terminalOpen} data-testid="programming-terminal-toggle">
              <span className="prog-chevron" aria-hidden="true">▾</span>
              <span className="prog-terminal-title">{t('devices.programming.terminal.title')}</span>
              <span className="prog-log-count">{p.log.length}</span>
              {p.busy && <span className="prog-terminal-live"><span className="prog-terminal-pulse" />{t('devices.programming.terminal.live')}</span>}
            </button>
            {!terminalOpen && p.log.length > 0 && (
              <span className={`prog-terminal-last prog-log-${p.log[p.log.length - 1].kind}`}>
                {p.log[p.log.length - 1].kind === 'out' ? '→' : p.log[p.log.length - 1].kind === 'in' ? '←' : '·'} {p.log[p.log.length - 1].text}
              </span>
            )}
            <button type="button" className="btn btn-link btn-sm prog-terminal-clear" onClick={p.clearLog} disabled={p.log.length === 0}>
              {t('devices.programming.terminal.clear')}
            </button>
          </div>
          <div className="prog-log" ref={logRef} data-testid="programming-log">
            {p.log.length === 0 && <span className="prog-log-empty">{t('devices.programming.logEmpty')}</span>}
            {p.log.map((entry, i) => (
              <div key={i} className={`prog-log-line prog-log-${entry.kind}`}>
                <span className="prog-log-time">{clock(entry.at)}</span>
                <span className="prog-log-dir">{entry.kind === 'out' ? '→' : entry.kind === 'in' ? '←' : '·'}</span>
                <span className="prog-log-text">{entry.text}</span>
              </div>
            ))}
          </div>
          {isAnepStaff && (
            <form className="prog-raw" onSubmit={(e) => { e.preventDefault(); p.sendRaw(rawInput); setRawInput(''); }}>
              <label htmlFor="prog-raw-input" className="prog-raw-prompt" title={t('devices.programming.rawLabel')}>›</label>
              <input id="prog-raw-input" className="prog-raw-input" value={rawInput} onChange={(e) => setRawInput(e.target.value)}
                     placeholder={t('devices.programming.terminal.prompt')} maxLength={160} disabled={p.busy} autoComplete="off" spellCheck={false}
                     data-testid="programming-raw-input" />
              <button type="submit" className="btn btn-secondary btn-sm" disabled={p.busy || !rawInput.trim()}>
                {t('devices.programming.rawSend')}
              </button>
            </form>
          )}
        </section>
      )}

      {pending && (
        <ConfirmChanges changes={pending} commands={commands} values={p.values} onConfirm={runPending} onCancel={() => setPending(null)} busy={p.writing} t={t} />
      )}
    </div>
  );
};
